import { createTab, fetchTab } from "@/api/hooks/tab";
import type { LabeledCoordinates } from "@/types/location";
import type { TabBase } from "@mm/metx-workbench.meteomatics.com";
import { type PayloadAction, createAction, createSelector, createSlice } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import type { RootState } from ".";

export type TabsState = {
  data: TabBase;
  isLoading: boolean;
  hasError: boolean;
};

const initialState: TabsState = {} as TabsState;

export const setTabTitle = createAction<{ profileId: number; tabId: number; title: string }>("tabs/setTabTitle");
export const setTabLocationMarkersEnabled = createAction<{ profileId: number; tabId: number; enabled: boolean }>(
  "tabs/setTabLocationMarkersEnabled",
);

/**
 * Action to change the location of all tools within the tab. Supported tool types: Plot, Meteogram, Table, Tephigram
 */
export const setAllToolLocation = createAction<{ location: LabeledCoordinates }>("tabs/setToolLocation");

const tabMetaSlice = createSlice({
  name: "tabs",
  initialState: initialState,
  reducers: {
    setTabOrder(state, action: PayloadAction<{ profileId: number; id: number; order: number }>) {
      const { id, order } = action.payload;

      const tab = state.data;
      if (!tab) {
        console.error("Could not find tab with id", id);
        return;
      }

      tab.order = order;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setTabTitle, (state, action) => {
        const { tabId, title } = action.payload;
        const tab = state.data;
        if (!tab) {
          console.error("Could not find tab with id", tabId);
          return;
        }
        if (tab.id !== tabId) {
          return;
        }
        tab.title = title;
      })
      .addCase(setTabLocationMarkersEnabled, (state, action) => {
        const { tabId, enabled } = action.payload;
        const tab = state.data;
        if (!tab) {
          console.error("Could not find tab with id", tabId);
          return;
        }
        if (tab.id !== tabId) {
          return;
        }
        tab.show_location_markers_on_map = enabled;
      })
      .addCase(fetchTab.pending, (state, action) => {
        if (!state) {
          // biome-ignore lint/style/noParameterAssign: TODO don't modify state directly??
          state = { data: {} as TabBase, isLoading: true, hasError: false };
        } else {
          state.isLoading = true;
          state.hasError = false;
        }
      })
      .addCase(fetchTab.fulfilled, (state, action) => {
        state.isLoading = false;
        state.hasError = false;
        state.data = action.payload;
      })
      .addCase(fetchTab.rejected, (state, action) => {
        state.hasError = !!action.error.message;
        state.isLoading = false;
      });
    builder.addCase(createTab.fulfilled, (state, action) => {
      state.data = action.payload;
    });
  },
});

const selectTabMeta = (state: RootState) => state.tabGroup.present.tabMeta;

/**
 * WARNING: useTab hook has issues with reactivity
 */
export function useTab() {
  return useSelector(selectTabMeta);
}

export function usePresentTab() {
  return useSelector((state: RootState) => state.tabGroup.present);
}

export function usePresentTabLayouts() {
  return useSelector((state: RootState) => state.tabGroup.present.layouts);
}

const makeSelectTabLocationMarkersEnabled = createSelector(
  [selectTabMeta, (_state: RootState, tabId: number) => tabId],
  (tabMeta, tabId) => {
    const tab = tabMeta.data;
    if (!tab || tab.id !== tabId) {
      return false;
    }
    return !!tab.show_location_markers_on_map;
  },
);

export function useTabLocationMarkersEnabled(tabId: number) {
  return useSelector((state: RootState) => makeSelectTabLocationMarkersEnabled(state, tabId));
}

// TODO return a slim tab, and use that in most of the places
// export function useCoreTab(id: number) {
export const { setTabOrder } = tabMetaSlice.actions;
export default tabMetaSlice.reducer;
